import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { Button, Card, Checkbox, Field, Input, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { RedirectToLogin } from '../../components/LoginModal';
import { FarmerPlaceFields, type FarmerPlace, type PlaceErrors } from './FarmerPlaceFields';
import { FarmerWelcomeCard } from './FarmerWelcomeCard';

/**
 * FARMER SELF-ONBOARDING, THE SIGNED-IN DOOR.
 *
 * Someone signed in with a number we had never seen, so they have a login and no farmer behind it:
 * no name, no place, no sales person. Until this form is done the login holds no permissions (the
 * server enforces it), so every app route lands here.
 *
 * The OTHER door is `/register`, where a person announces themselves as a farmer first and verifies
 * the number inside the form. Both end at the same `POST /onboarding/complete`, and both ask for a
 * place with the same `FarmerPlaceFields`.
 *
 * Every answer is saved as it is typed, so a farmer who leaves half-way resumes where they stopped,
 * and the office's call list can see how far they got.
 */

type Draft = FarmerPlace & { fullName?: string };
type FieldErrors = PlaceErrors & Partial<Record<'name' | 'consent', string>>;

export function WelcomePage() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!user) return <RedirectToLogin from={location.pathname} />;
  if (!user.onboardingRequired) return <Navigate to="/home" replace />;
  return <WelcomeForm />;
}

function WelcomeForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logout = useAuthStore((s) => s.logout);
  const syncUser = useAuthStore((s) => s.syncUser);

  const { data: me, isLoading } = useQuery({ queryKey: ['onboarding-me'], queryFn: () => request<any>({ url: '/onboarding/me' }) });

  const [d, setD] = useState<Draft>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<any | null>(null);
  const seeded = useRef(false);

  // Resume the saved draft once.
  useEffect(() => {
    if (seeded.current || !me) return;
    seeded.current = true;
    setD(me.draft ?? {});
  }, [me]);

  // Autosave — debounced, and only what changed since the last save.
  const lastSaved = useRef<string>('');
  useEffect(() => {
    if (!seeded.current) return;
    const body = JSON.stringify(d);
    if (body === lastSaved.current) return;
    const t = window.setTimeout(() => {
      lastSaved.current = body;
      request({ url: '/onboarding/draft', method: 'PATCH', data: { ...d, platform: 'WEB' } }).catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(t);
  }, [d]);

  const set = (patch: Draft) => {
    setD((prev) => ({ ...prev, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      if ('fullName' in patch) delete next.name;
      if ('stateCode' in patch) delete next.state;
      if ('districtId' in patch || 'districtText' in patch) delete next.district;
      if ('village' in patch) delete next.village;
      if ('pincode' in patch) delete next.pincode;
      return next;
    });
  };

  const submit = async () => {
    const local = validateFarmerForm(d, consent);
    setErrors(local);
    if (Object.keys(local).length) return;
    setSaving(true);
    try {
      setDone(await request<any>({
        url: '/onboarding/complete', method: 'POST', data: { ...d, consent: true, platform: 'WEB' },
      }));
    } catch (e: any) {
      const err = e?.response?.data?.error;
      if (err?.code === 'VALIDATION_FAILED' && err.details) setErrors(err.details);
      message.error(err?.message ?? 'Could not save your details. Please try again.');
    } finally { setSaving(false); }
  };

  const enterApp = async () => {
    try {
      const fresh = await request<any>({ url: '/auth/me' });
      syncUser({
        fullName: fresh.fullName, role: fresh.role, roles: fresh.roles ?? [fresh.role],
        permissions: fresh.permissions ?? [], onboardingRequired: !!fresh.onboardingRequired,
      });
      qc.invalidateQueries({ queryKey: ['auth-me'] });
    } catch { syncUser({ onboardingRequired: false }); }
    navigate('/home', { replace: true });
  };

  const support = done?.support ?? me?.support;

  return (
    <div className="min-h-screen bg-surface-2 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between">
          <img src="/logo.png" alt="Green Bay Biotech International" className="h-12 object-contain" />
          <Button variant="ghost" size="sm" icon={<LogOut size={16} />} onClick={() => { logout(); navigate('/'); }}>Sign out</Button>
        </div>

        {done ? (
          <FarmerWelcomeCard done={done} support={support} onContinue={enterApp} />
        ) : (
          <Card>
            <h1 className="type-section-title text-content">Tell us about yourself</h1>
            <p className="mt-1 type-body text-muted">So your local Green Bay representative can reach you. It takes about a minute.</p>

            {isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : (
              <div className="mt-5 flex flex-col gap-4">
                <Field label="Full name" required htmlFor="ob-name" error={errors.name}>
                  <Input id="ob-name" autoComplete="name" value={d.fullName ?? ''} invalid={!!errors.name}
                    placeholder="e.g. Ramesh Patil" maxLength={120} onChange={(e) => set({ fullName: e.target.value })} />
                </Field>

                <FarmerPlaceFields value={d} onChange={set} errors={errors} />

                <div>
                  <Checkbox checked={consent} onCheckedChange={(c) => { setConsent(!!c); setErrors((e) => ({ ...e, consent: undefined })); }}
                    label="I agree that Green Bay Biotech may store these details and contact me about its products and services." />
                  {errors.consent && <p className="mt-1 type-caption text-danger-600">{errors.consent}</p>}
                </div>

                <Button variant="primary" block loading={saving} onClick={submit}>Save and continue</Button>
                {support?.phone && (
                  <p className="text-center type-caption text-muted">
                    Need help? Call <a className="text-brand-700" href={`tel:${support.phone.replace(/\s/g, '')}`}>{support.phone}</a>
                  </p>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * The five answers, checked the same way on both doors — and deliberately the same rule the server
 * applies (`completeProblems`), so the form never lets through something the endpoint will refuse,
 * and never refuses something it would accept.
 */
export function validateFarmerForm(d: { fullName?: string } & FarmerPlace, consent: boolean): PlaceErrors & Partial<Record<'name' | 'consent', string>> {
  const out: PlaceErrors & Partial<Record<'name' | 'consent', string>> = {};
  if (!d.fullName?.trim()) out.name = 'Enter your full name.';
  if (!d.stateCode) out.state = 'Select your state.';
  // Either answer counts: picked from our list, or typed where we list no districts.
  if (!d.districtId && !d.districtText?.trim()) out.district = 'Select or type your district.';
  if (!d.village?.trim()) out.village = 'Enter your village or town.';
  if (!/^[1-9]\d{5}$/.test(d.pincode ?? '')) out.pincode = 'Enter your 6-digit pincode.';
  if (!consent) out.consent = 'Please agree so we can connect you with your local representative.';
  return out;
}
